/* Import External Modules */
import { ns } from '@leverege/i18n';
import React from 'react';
/* Import Leverege Modules */
import { Button, Checkbox, Flex, Label, NumericInput, PropertyGrid, Selector, Text, TextInput } from '@leverege/ui-elements';
import { useValueChange } from '@leverege/ui-hooks';

/* Import Local Modules */
import UnitFormatter from "./GeoPointFormatter.js"; // eslint-disable-line
import Model from "./GeoPointFormatterModel.js";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const {
  t
} = ns('ui-attributes.GeoPointFormatterEditor');

/**
 * Editor for geo-point formatter.
 * @param {Object} props.value - Value formatter object.
 * @param {String} props.value.type - Value type, e.g. attr.formatter.geoPoint.
 * @param {String} props.value.format - Format string, e.g. MM/dd.
 */
export default function GeoPointFormatterEditor(props) {
  // Get parameters
  const {
    value
  } = props;
  const onModelChange = useValueChange(Model, props);
  const coordinates = {
    lat: -77.251,
    lon: 39.19
  };
  const templates = [];

  // Validate parameters
  if (value == null) {
    return null;
  }

  // Set selection to not-set when value is null or empty
  if (!value?.format) {
    templates.push({
      value: value?.format,
      name: t('notSet')
    });
  }

  // Initialize formatcoords compatible templates, see https://github.com/nerik/formatcoords
  templates.push(...[{
    value: 'FFf',
    name: t('dms')
  }, {
    value: 'Ff',
    name: t('ddm')
  }, {
    value: 'f',
    name: t('dd')
  }, {
    value: '-d',
    name: t('ddNoUnits')
  }]);

  // Add examples to templates
  templates.forEach(template => {
    if (!template.value) {
      return;
    }
    const options = {
      format: template.value,
      latPrefix: '',
      decimalPlaces: value?.decimalPlaces
    };
    const example = UnitFormatter.format(coordinates, options)[0];
    template.name += ` (e.g. ${example})`;
  });

  // Set selection to custom when using non-templated format
  if (value?.format && !templates.map(template => template.value).includes(value?.format)) {
    templates.push({
      value: value?.format,
      name: t('custom')
    });
  }

  // Get format preview
  let previewLine1 = t('notAvailable');
  let previewLine2 = null;
  try {
    const rawPreview = UnitFormatter.format(coordinates, value);
    previewLine1 = rawPreview.length > 0 ? rawPreview[0] : previewLine1;
    previewLine2 = rawPreview.length > 2 ? rawPreview[2] : previewLine2;
  } catch (error) {
    previewLine1 = t('invalidFormat');
  }

  // Render editor
  return /*#__PURE__*/_jsxs(PropertyGrid, {
    style: {
      backgroundColor: 'var(--color-white)',
      border: 0
    },
    children: [/*#__PURE__*/_jsx(PropertyGrid.Item, {
      stretch: true,
      label: t('geopointFormat'),
      children: /*#__PURE__*/_jsx(Selector, {
        eventData: "setFormat",
        value: Model.getFormat(value),
        values: templates,
        onChange: onModelChange
      })
    }), /*#__PURE__*/_jsx(PropertyGrid.Item, {
      stretch: true,
      label: t('formatCode'),
      children: /*#__PURE__*/_jsxs(Flex, {
        children: [/*#__PURE__*/_jsx(TextInput, {
          size: 24,
          eventData: "setFormat",
          hint: t('formatCodeHint'),
          value: Model.getFormat(value),
          onChange: onModelChange
        }), /*#__PURE__*/_jsx(Button, {
          variant: "tertiary",
          icon: "fa fa-external-link",
          onClick: () => window.open('https://github.com/nerik/formatcoords'),
          children: t('viewDocs')
        })]
      })
    }), /*#__PURE__*/_jsx(PropertyGrid.Item, {
      stretch: true,
      label: t('decimalPlaces'),
      children: /*#__PURE__*/_jsxs(Flex, {
        variant: "rowM",
        align: "center",
        children: [/*#__PURE__*/_jsx(NumericInput, {
          min: 0,
          max: 16,
          size: 4,
          allowError: false,
          eventData: "setDecimalPlaces",
          hint: t('decimalPlacesHint'),
          value: Model.getDecimalPlaces(value),
          onChange: onModelChange
        }), /*#__PURE__*/_jsxs(Flex, {
          style: {
            width: 128
          },
          variant: "rowM",
          align: "center",
          children: [/*#__PURE__*/_jsx(Label, {
            variant: "input",
            children: "Single Line"
          }), /*#__PURE__*/_jsx(Checkbox, {
            value: Model.getSingleLine(value),
            onChange: onModelChange,
            eventData: "setSingleLine",
            style: {
              height: 10
            }
          })]
        })]
      })
    }), /*#__PURE__*/_jsx(PropertyGrid.Item, {
      fill: true,
      label: t('preview'),
      children: /*#__PURE__*/_jsxs("div", {
        children: [/*#__PURE__*/_jsx(Text, {
          children: previewLine1
        }), previewLine2 && /*#__PURE__*/_jsx(Text, {
          children: previewLine2
        })]
      })
    })]
  });
}